import type { Question } from '../types'

/**
 * Public trace code for a question. It deliberately contains no answer or
 * solution data, while the legacy `id` stays the durable attempt/storage key.
 * Example: SM-TSA-G11-SEQ-M3-ORIG-0K9Z3F1
 */
const SMART_ID = /^SM-(HSA|TSA|VACT)-(G10|G11|G12|GX)-[A-Z0-9]{2,12}-M[123]-(ORIG|GEN)-[A-Z0-9]{7}$/

export function isSmartQuestionId(value: string | undefined): value is string {
  return Boolean(value && SMART_ID.test(value))
}

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .toLowerCase()
}

function includesAny(text: string, values: string[]) {
  return values.some(value => text.includes(value))
}

function familyAndGrade(question: Question) {
  // Taxonomy is the primary contract.  Text heuristics remain only for legacy
  // items, whose old metadata was too broad to classify safely.
  if (question.knowledge.includes('functions')) return { family: 'CALC', grade: 'G12' }
  if (question.knowledge.includes('calculus')) return { family: 'CALC', grade: 'G12' }
  if (question.knowledge.includes('oxyz')) return { family: 'OXYZ', grade: 'G12' }
  if (question.knowledge.includes('exponents-logarithms')) return { family: 'LIM', grade: 'G11' }
  if (question.knowledge.includes('sequences')) return { family: 'SEQ', grade: 'G11' }
  if (question.knowledge.includes('trigonometry')) return { family: 'TRIG', grade: 'G11' }
  if (question.knowledge.includes('probability-statistics')) return { family: 'PROB', grade: 'G12' }
  if (question.knowledge.includes('data')) return { family: 'STAT', grade: 'G11' }
  if (question.knowledge.includes('logic')) return { family: 'LOGIC', grade: 'GX' }
  if (question.knowledge.includes('modelling')) return { family: 'MODEL', grade: 'GX' }
  const text = normalize(`${question.domain} ${question.topic} ${question.tags.join(' ')}`)
  if (includesAny(text, ['oxyz', 'mat cau', 'khong gian', 'spatial'])) return { family: 'OXYZ', grade: 'G12' }
  if (includesAny(text, ['dao ham', 'tich phan', 'nguyen ham', 'ham so', 'toi uu'])) return { family: 'CALC', grade: 'G12' }
  if (includesAny(text, ['gioi han', 'lien tuc', 'mu logarit', 'logarit'])) return { family: 'LIM', grade: 'G11' }
  if (includesAny(text, ['luong giac', 'sin', 'cos', 'tan'])) return { family: 'TRIG', grade: 'G11' }
  if (includesAny(text, ['cap so', 'day so', 'tra gop', 'tai chinh', 'cashflow', 'annuity'])) return { family: 'SEQ', grade: 'G11' }
  if (includesAny(text, ['bayes', 'xac suat co dieu kien', 'phan phoi'])) return { family: 'PROB', grade: 'G12' }
  if (includesAny(text, ['to hop', 'counting', 'ghe ngoi'])) return { family: 'COUNT', grade: 'G10' }
  if (includesAny(text, ['thong ke', 'mau ghep nhom', 'du lieu'])) return { family: 'STAT', grade: 'G11' }
  if (includesAny(text, ['quy hoach dong', 'dynamic-programming', 'duong di'])) return { family: 'DP', grade: 'GX' }
  if (includesAny(text, ['quy hoach tuyen tinh', 'linear-programming'])) return { family: 'LP', grade: 'GX' }
  if (includesAny(text, ['logic', 'suy luan', 'dieu kien'])) return { family: 'LOGIC', grade: 'GX' }
  if (includesAny(text, ['mo hinh', 'modelling', 'modeling'])) return { family: 'MODEL', grade: 'GX' }
  return { family: 'MATH', grade: 'GX' }
}

function stableHash(value: string) {
  let hash = 2166136261
  for (const character of value) {
    hash ^= character.charCodeAt(0)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36).toUpperCase().padStart(7, '0')
}

export function getSmartQuestionId(question: Question) {
  if (question.contentCode) return question.contentCode
  const { family, grade } = familyAndGrade(question)
  const origin = question.tags.some(tag => ['parameterized', 'session-variant', 'tsa-math-generator'].includes(tag)) ? 'GEN' : 'ORIG'
  const trace = stableHash(`${question.id}|${question.variantGroup}`)
  return `SM-${question.exam}-${grade}-${family}-${question.cognitiveLevel}-${origin}-${trace}`
}
